import { cn } from "@-/com.npmjs.cn";
import { Pressable, View } from "@-/com.npmjs.react-native";
import { Icon } from "@-/dev.improve.haniverse.components.ui.icon";
import { Text } from "@-/dev.improve.haniverse.components.ui.text";

/**
 * Surface-header controls for a dated view, after Notion Calendar's header: a Today preset that
 * returns to the period containing today, then previous/next chevrons. Like Notion's date badge,
 * Today fills in while the view is away from today and stays quiet while today is in view. The
 * `compact` variant draws Today as a calendar icon between the chevrons instead of a labeled
 * button before them.
 */
export function PeriodNavigationControls({
  disabled = false,
  isNextDisabled = false,
  isPreviousDisabled = false,
  isTodayInView,
  nextLabel,
  onNext,
  onPrevious,
  onToday,
  previousLabel,
  testID,
  todayHint,
  variant = "labeled",
}: {
  /** Disables every control, e.g. while the view is still loading. */
  disabled?: boolean;
  isNextDisabled?: boolean;
  isPreviousDisabled?: boolean;
  isTodayInView: boolean;
  nextLabel: string;
  onNext: () => void;
  onPrevious: () => void;
  /** Omit when the view has no today to return to (an all-time range); Today then hides. */
  onToday?: () => void;
  previousLabel: string;
  testID: string;
  todayHint?: string;
  variant?: "labeled" | "compact";
}) {
  const isCompact = variant === "compact";
  const today =
    onToday === undefined ? null : (
      <Pressable
        accessibilityHint={todayHint}
        accessibilityLabel="Today"
        accessibilityRole="button"
        accessibilityState={{ disabled, selected: isTodayInView }}
        className={cn(
          "items-center justify-center rounded-xs",
          isCompact ? "size-7" : "mr-1 h-7 border px-2.5",
          isTodayInView
            ? isCompact
              ? "active:bg-accent"
              : "border-border bg-surface active:bg-accent"
            : "border-primary bg-primary active:bg-primary/80",
          disabled && "opacity-50",
        )}
        disabled={disabled}
        hitSlop={4}
        onPress={onToday}
        testID={`${testID}-today`}
      >
        {isCompact ? (
          <Icon
            className={isTodayInView ? "text-muted-foreground" : "text-primary-foreground"}
            name="LuCalendar"
            size={16}
          />
        ) : (
          <Text
            className={cn(
              "text-xs font-medium",
              isTodayInView ? "text-foreground" : "text-primary-foreground",
            )}
          >
            Today
          </Text>
        )}
      </Pressable>
    );
  return (
    <View className={cn("flex-row items-center", !isCompact && "gap-1")} testID={testID}>
      {isCompact ? null : today}
      <PeriodNavigationChevron
        accessibilityLabel={previousLabel}
        compact={isCompact}
        disabled={disabled || isPreviousDisabled}
        name="FiChevronLeft"
        onPress={onPrevious}
        testID={`${testID}-previous`}
      />
      {isCompact ? today : null}
      <PeriodNavigationChevron
        accessibilityLabel={nextLabel}
        compact={isCompact}
        disabled={disabled || isNextDisabled}
        name="FiChevronRight"
        onPress={onNext}
        testID={`${testID}-next`}
      />
    </View>
  );
}

function PeriodNavigationChevron({
  accessibilityLabel,
  compact,
  disabled,
  name,
  onPress,
  testID,
}: {
  accessibilityLabel: string;
  compact: boolean;
  disabled: boolean;
  name: "FiChevronLeft" | "FiChevronRight";
  onPress: () => void;
  testID: string;
}) {
  return (
    <Pressable
      accessibilityLabel={accessibilityLabel}
      accessibilityRole="button"
      accessibilityState={{ disabled }}
      className={cn(
        "items-center justify-center rounded-xs active:bg-accent",
        compact ? "size-7" : "size-8",
        disabled && "opacity-30",
      )}
      disabled={disabled}
      hitSlop={4}
      onPress={onPress}
      testID={testID}
    >
      <Icon className="text-muted-foreground" name={name} size={compact ? 18 : 20} />
    </Pressable>
  );
}
